import type {
    PuppySectionViewExtension,
    SectionViewDocumentSnapshot,
    SectionViewOffsetMethod,
    SectionViewPlaneDefinitionSnapshot,
    SectionViewPlaneIndex,
    SectionViewReferenceKind,
    SectionViewReferenceSnapshot,
    SectionViewVectorTuple,
} from '../../../types/section-view/SectionViewTypes';

/** assembly.json.extensions 中保存剖面视图定义的字段名。 */
export const PUPPY_SECTION_VIEW_EXTENSION_KEY = 'sectionView';

/** 离线包剖面视图扩展第一版协议。 */
export const PUPPY_SECTION_VIEW_SCHEMA_VERSION = 'puppy-section-view-extension-v1' as const;

const SECTION_INDEXES = new Set<number>([0, 1, 2]);
const OFFSET_METHODS = new Set<SectionViewOffsetMethod>(['reference-plane', 'selected-plane']);
const REFERENCE_KINDS = new Set<SectionViewReferenceKind>([
    'standard-plane',
    'datum-plane',
    'planar-face',
    'world-frame',
]);
const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
const MINIMUM_VECTOR_LENGTH_SQUARED = 1e-12;

/**
 * 判断未知值是否为普通记录对象。
 *
 * @param value 未知值。
 * @returns 是否为普通记录对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

/**
 * 读取有限数字。
 *
 * @param value 未知值。
 * @param path 字段路径。
 * @returns 有限数字。
 */
function readFiniteNumber(value: unknown, path: string): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(`PuppyCAD 离线包剖面视图字段必须是有限数字: ${path}`);
    }
    return value;
}

/**
 * 读取布尔值。
 *
 * @param value 未知值。
 * @param path 字段路径。
 * @returns 布尔值。
 */
function readBoolean(value: unknown, path: string): boolean {
    if (typeof value !== 'boolean') {
        throw new Error(`PuppyCAD 离线包剖面视图字段必须是布尔值: ${path}`);
    }
    return value;
}

/**
 * 读取非空字符串。
 *
 * @param value 未知值。
 * @param path 字段路径。
 * @returns 去除两端空白后的字符串。
 */
function readNonEmptyString(value: unknown, path: string): string {
    if (typeof value !== 'string' || !value.trim()) {
        throw new Error(`PuppyCAD 离线包剖面视图字段必须是非空字符串: ${path}`);
    }
    return value.trim();
}

/**
 * 读取三维向量并创建独立快照。
 *
 * @param value 未知值。
 * @param path 字段路径。
 * @param requiresDirection 是否要求非零方向。
 * @returns 已校验三元组。
 */
function readVectorTuple(
    value: unknown,
    path: string,
    requiresDirection: boolean,
): SectionViewVectorTuple {
    if (!Array.isArray(value) || value.length !== 3) {
        throw new Error(`PuppyCAD 离线包剖面视图向量必须包含三个分量: ${path}`);
    }
    const tuple = [
        readFiniteNumber(value[0], `${path}[0]`),
        readFiniteNumber(value[1], `${path}[1]`),
        readFiniteNumber(value[2], `${path}[2]`),
    ] as const;
    const lengthSquared = tuple[0] ** 2 + tuple[1] ** 2 + tuple[2] ** 2;
    if (requiresDirection && lengthSquared <= MINIMUM_VECTOR_LENGTH_SQUARED) {
        throw new Error(`PuppyCAD 离线包剖面视图方向不能是零向量: ${path}`);
    }
    return tuple;
}

/**
 * 解析剖切参考快照。
 *
 * @param value 未知 JSON 值。
 * @param path 字段路径。
 * @returns 已校验参考快照。
 */
function parseReference(value: unknown, path: string): SectionViewReferenceSnapshot {
    if (!isRecord(value)) {
        throw new Error(`PuppyCAD 离线包剖面视图参考必须是对象: ${path}`);
    }
    if (typeof value.kind !== 'string' || !REFERENCE_KINDS.has(value.kind as SectionViewReferenceKind)) {
        throw new Error(`PuppyCAD 离线包剖面视图参考类型无效: ${path}.kind`);
    }
    const kind = value.kind as SectionViewReferenceKind;
    const datumPlaneId = value.datumPlaneId === undefined
        ? undefined
        : readFiniteNumber(value.datumPlaneId, `${path}.datumPlaneId`);
    const objectId = value.objectId === undefined
        ? undefined
        : readNonEmptyString(value.objectId, `${path}.objectId`);
    const faceIndex = value.faceIndex === undefined
        ? undefined
        : readFiniteNumber(value.faceIndex, `${path}.faceIndex`);

    if (kind === 'datum-plane' && datumPlaneId === undefined) {
        throw new Error(`PuppyCAD 离线包剖面视图基准面参考缺少 datumPlaneId: ${path}`);
    }
    if (datumPlaneId !== undefined && (!Number.isInteger(datumPlaneId) || datumPlaneId < 0)) {
        throw new Error(`PuppyCAD 离线包剖面视图基准面 ID 必须是非负整数: ${path}.datumPlaneId`);
    }
    if (kind === 'planar-face' && (objectId === undefined || faceIndex === undefined)) {
        throw new Error(`PuppyCAD 离线包剖面视图实体面参考缺少 objectId 或 faceIndex: ${path}`);
    }
    if (faceIndex !== undefined && (!Number.isInteger(faceIndex) || faceIndex < 0)) {
        throw new Error(`PuppyCAD 离线包剖面视图面索引必须是非负整数: ${path}.faceIndex`);
    }

    return {
        kind,
        label: readNonEmptyString(value.label, `${path}.label`),
        ...(datumPlaneId === undefined ? {} : { datumPlaneId }),
        ...(objectId === undefined ? {} : { objectId }),
        ...(faceIndex === undefined ? {} : { faceIndex }),
        origin: readVectorTuple(value.origin, `${path}.origin`, false),
        normal: readVectorTuple(value.normal, `${path}.normal`, true),
        xAxis: readVectorTuple(value.xAxis, `${path}.xAxis`, true),
        yAxis: readVectorTuple(value.yAxis, `${path}.yAxis`, true),
    };
}

/**
 * 解析单个剖面定义。
 *
 * @param value 未知 JSON 值。
 * @param path 字段路径。
 * @returns 已校验剖面定义。
 */
function parsePlaneDefinition(value: unknown, path: string): SectionViewPlaneDefinitionSnapshot {
    if (!isRecord(value)) {
        throw new Error(`PuppyCAD 离线包剖面视图剖面定义必须是对象: ${path}`);
    }
    const rawIndex = readFiniteNumber(value.index, `${path}.index`);
    if (!Number.isInteger(rawIndex) || !SECTION_INDEXES.has(rawIndex)) {
        throw new Error(`PuppyCAD 离线包剖面视图剖面索引必须是 0、1 或 2: ${path}.index`);
    }
    const color = readNonEmptyString(value.color, `${path}.color`);
    if (!HEX_COLOR_PATTERN.test(color)) {
        throw new Error(`PuppyCAD 离线包剖面视图颜色必须是 #RRGGBB: ${path}.color`);
    }
    return {
        index: rawIndex as SectionViewPlaneIndex,
        enabled: readBoolean(value.enabled, `${path}.enabled`),
        reference: parseReference(value.reference, `${path}.reference`),
        reverse: readBoolean(value.reverse, `${path}.reverse`),
        offsetMillimeters: readFiniteNumber(value.offsetMillimeters, `${path}.offsetMillimeters`),
        rotationXDegrees: readFiniteNumber(value.rotationXDegrees, `${path}.rotationXDegrees`),
        rotationYDegrees: readFiniteNumber(value.rotationYDegrees, `${path}.rotationYDegrees`),
        color: color.toLowerCase(),
    };
}

/**
 * 解析文档级剖面快照并创建深拷贝。
 *
 * @param value 未知 JSON 值。
 * @param path 字段路径。
 * @returns 已校验文档快照。
 */
function parseDocumentSnapshot(value: unknown, path: string): SectionViewDocumentSnapshot {
    if (!isRecord(value)) {
        throw new Error(`PuppyCAD 离线包剖面视图快照必须是对象: ${path}`);
    }
    if (typeof value.offsetMethod !== 'string'
        || !OFFSET_METHODS.has(value.offsetMethod as SectionViewOffsetMethod)) {
        throw new Error(`PuppyCAD 离线包剖面视图偏移方法无效: ${path}.offsetMethod`);
    }
    if (!Array.isArray(value.sections) || value.sections.length < 1 || value.sections.length > 3) {
        throw new Error(`PuppyCAD 离线包剖面视图必须包含 1 至 3 个剖面定义: ${path}.sections`);
    }
    const sections = value.sections
        .map((section, index) => parsePlaneDefinition(section, `${path}.sections[${index}]`))
        .sort((left, right) => left.index - right.index);
    const uniqueIndexes = new Set(sections.map((section) => section.index));
    if (uniqueIndexes.size !== sections.length || sections[0]?.index !== 0) {
        throw new Error(`PuppyCAD 离线包剖面视图剖面索引必须唯一且包含剖面 1: ${path}.sections`);
    }
    if (!sections[0].enabled) {
        throw new Error(`PuppyCAD 离线包剖面视图剖面 1 必须启用: ${path}.sections[0].enabled`);
    }
    const sectionTwo = sections.find((section) => section.index === 1);
    const sectionThree = sections.find((section) => section.index === 2);
    if (sectionThree?.enabled && !sectionTwo?.enabled) {
        throw new Error(`PuppyCAD 离线包剖面视图剖面 3 依赖已启用的剖面 2: ${path}.sections`);
    }
    return {
        enabled: readBoolean(value.enabled, `${path}.enabled`),
        showCap: readBoolean(value.showCap, `${path}.showCap`),
        offsetMethod: value.offsetMethod as SectionViewOffsetMethod,
        sections,
    };
}

/**
 * 根据文档快照创建离线包剖面视图扩展。
 *
 * @param snapshot 文档快照；没有剖面定义时可传空值。
 * @returns 版本化扩展；空值时返回 undefined。
 */
export function createPuppySectionViewExtension(
    snapshot: SectionViewDocumentSnapshot | null | undefined,
): PuppySectionViewExtension | undefined {
    if (!snapshot || (Array.isArray(snapshot.sections) && snapshot.sections.length === 0)) {
        return undefined;
    }
    return {
        schemaVersion: PUPPY_SECTION_VIEW_SCHEMA_VERSION,
        snapshot: parseDocumentSnapshot(snapshot, 'snapshot'),
    };
}

/**
 * 解析未知剖面视图扩展。
 *
 * @param value 未知 JSON 值。
 * @returns 已校验扩展。
 * @throws 当版本或字段无效时抛出明确错误。
 */
export function parsePuppySectionViewExtension(value: unknown): PuppySectionViewExtension {
    if (!isRecord(value)) {
        throw new Error('PuppyCAD 离线包剖面视图扩展必须是对象');
    }
    if (value.schemaVersion !== PUPPY_SECTION_VIEW_SCHEMA_VERSION) {
        throw new Error(`不支持的 PuppyCAD 离线包剖面视图扩展版本: ${String(value.schemaVersion)}`);
    }
    return {
        schemaVersion: PUPPY_SECTION_VIEW_SCHEMA_VERSION,
        snapshot: parseDocumentSnapshot(value.snapshot, 'snapshot'),
    };
}

/**
 * 从 assembly.json extensions 读取剖面视图扩展。
 *
 * @param extensions 装配文档扩展集合。
 * @returns 存在时返回已校验扩展，否则返回 undefined。
 */
export function readPuppySectionViewExtensionFromAssemblyExtensions(
    extensions: Readonly<Record<string, unknown>> | undefined,
): PuppySectionViewExtension | undefined {
    const value = extensions?.[PUPPY_SECTION_VIEW_EXTENSION_KEY];
    return value === undefined ? undefined : parsePuppySectionViewExtension(value);
}

/**
 * 把剖面视图扩展写入 assembly.json extensions。
 *
 * @param extensions 原扩展集合。
 * @param sectionViewExtension 待写入扩展；undefined 表示删除自身字段。
 * @returns 保留其他扩展后的新对象；没有任何扩展时返回 undefined。
 */
export function writePuppySectionViewExtensionToAssemblyExtensions(
    extensions: Readonly<Record<string, unknown>> | undefined,
    sectionViewExtension: PuppySectionViewExtension | undefined,
): Record<string, unknown> | undefined {
    const nextExtensions = extensions ? { ...extensions } : {};
    if (sectionViewExtension) {
        nextExtensions[PUPPY_SECTION_VIEW_EXTENSION_KEY] = sectionViewExtension;
    } else {
        delete nextExtensions[PUPPY_SECTION_VIEW_EXTENSION_KEY];
    }
    return Object.keys(nextExtensions).length > 0 ? nextExtensions : undefined;
}
